// src/components/admin/ProductList.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import AddBoxIcon from '@mui/icons-material/AddBoxOutlined';
import { AdminShell } from './Sidebar';
import { ProductProfilePageRoute, CreateProductPagePageRoute } from '../../consts';
import {
  StatCard, RefreshButton, FilterPill, SearchField,
  TableCard, LoadingState, EmptyState, Pagination,
} from '../ui/CrmPage';

const formatPrice = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return new Intl.NumberFormat('ru-KZ').format(n) + ' ₸';
};

function desc(a, b, getVal) {
  const av = getVal(a); const bv = getVal(b);
  if (bv == null && av != null) return -1;
  if (av == null && bv != null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
const getComparator = (order, getter) =>
  order === 'desc' ? (a, b) => desc(a, b, getter) : (a, b) => -desc(a, b, getter);
const stableSort = (arr, comp) =>
  arr.map((el, i) => [el, i]).sort((a, b) => { const o = comp(a[0], b[0]); return o || a[1] - b[1]; }).map((el) => el[0]);

const sortIcon = (active, dir) => (
  <span className={`crmpage__sort ${active ? 'is-active' : ''}`} aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

export default function ProductList() {
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const normalizeCategories = (arr) =>
    (Array.isArray(arr) ? arr : [])
      .map((c) => ({
        id: c.id ?? c.id_product_category ?? c.category_id ?? c.value ?? c.code ?? '',
        name: c.name ?? c.category_name ?? c.label ?? '',
      }))
      .filter((c) => c.id !== '' && c.name !== '');
  const catNameById = (cid) => categories.find((c) => String(c.id) === String(cid))?.name || '';

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true); setError('');
      try {
        const [prodRes, catsRes] = await Promise.all([
          fetch(`${apiBase}v1/products?sortBy=id&orderBy=asc`, { signal: ctrl.signal }),
          fetch(`${apiBase}v1/productCategories`, { signal: ctrl.signal }),
        ]);
        if (!prodRes.ok) {
          const t = await prodRes.text().catch(() => '');
          throw new Error(`HTTP ${prodRes.status} ${prodRes.statusText} ${t}`.trim());
        }
        const prodData = await prodRes.json();
        let cats = [];
        if (catsRes.ok) {
          const catsJson = await catsRes.json().catch(() => []);
          cats = normalizeCategories(catsJson);
        }
        setRows(Array.isArray(prodData) ? prodData : []);
        setCategories(cats);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, reloadKey]);

  const [q, setQ] = useState('');
  const [catFilter, setCatFilter] = useState('all');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const totals = useMemo(() => {
    const total = rows.length;
    const withPhoto = rows.filter((r) => !!r.product_photo).length;
    const cats = new Set(rows.map((r) => r.id_product_category).filter(Boolean)).size;
    const avgPrice = rows.length
      ? rows.reduce((acc, r) => acc + (Number(r.price) || 0), 0) / rows.length
      : 0;
    return { total, withPhoto, cats, avgPrice };
  }, [rows]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((p) => {
      if (catFilter !== 'all' && String(p.id_product_category) !== catFilter) return false;
      if (!term) return true;
      return (
        (p.name || '').toLowerCase().includes(term) ||
        (p.description || '').toLowerCase().includes(term)
      );
    });
  }, [rows, q, catFilter]);

  const sortGetter = useMemo(() => {
    switch (orderBy) {
      case 'id':         return (p) => Number(p.id || 0);
      case 'name':       return (p) => (p.name || '').toLowerCase();
      case 'category':   return (p) => catNameById(p.id_product_category).toLowerCase();
      case 'id_measure': return (p) => String(p.id_measure ?? '');
      case 'price':      return (p) => Number(p.price ?? 0);
      default:           return (p) => (p[orderBy] ?? '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, categories]);

  const sorted = useMemo(
    () => stableSort(filtered, getComparator(order, sortGetter)),
    [filtered, order, sortGetter]
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const requestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const hasFilters = q || catFilter !== 'all';
  const resetFilters = () => { setQ(''); setCatFilter('all'); setPage(1); };

  return (
    <AdminShell
      breadcrumbs="Товары"
      title="Товары"
      subtitle="Склад, цены, доступность"
      actions={
        <button
          type="button"
          className="adminbtn adminbtn--primary"
          onClick={() => navigate(CreateProductPagePageRoute)}
        >
          <AddBoxIcon sx={{ fontSize: 18 }} />
          Добавить
        </button>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          <section className="crmpage__stats">
            <StatCard label="Всего"        value={totals.total}     accent="neutral" />
            <StatCard label="С фото"       value={totals.withPhoto} accent="success" />
            <StatCard label="Категорий"    value={totals.cats}      accent="brand" />
            <StatCard label="Средняя цена" value={formatPrice(totals.avgPrice)} accent="info" wide />
            <RefreshButton onClick={() => setReloadKey((k) => k + 1)} />
          </section>

          <section className="crmpage__filters">
            <SearchField
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Поиск: название, описание…"
            />
            <div className="crmpage__pills">
              <FilterPill active={catFilter === 'all'} onClick={() => { setCatFilter('all'); setPage(1); }}>Все категории</FilterPill>
              {categories.map((c) => (
                <FilterPill
                  key={c.id}
                  active={catFilter === String(c.id)}
                  onClick={() => { setCatFilter(String(c.id)); setPage(1); }}
                >
                  {c.name}
                </FilterPill>
              ))}
            </div>
            {hasFilters && (
              <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
            )}
          </section>

          {error && <div className="crmpage__alert crmpage__alert--error">Не удалось загрузить товары. {error}</div>}

          <TableCard>
            {loading ? (
              <LoadingState text="Загружаем товары…" />
            ) : paged.length === 0 ? (
              <EmptyState
                title="Товаров не найдено"
                text={hasFilters ? 'Сбросьте фильтры или измените запрос.' : 'Добавьте первый товар.'}
              />
            ) : (
              <>
                <div className="crmpage__table-wrap">
                  <table className="crmpage__table">
                    <colgroup>
                      <col style={{ width: 70 }} />
                      <col />
                      <col style={{ width: 200 }} />
                      <col style={{ width: 100 }} />
                      <col style={{ width: 140 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th onClick={() => requestSort('id')}         style={{ cursor: 'pointer' }}>ID {sortIcon(orderBy === 'id', order)}</th>
                        <th onClick={() => requestSort('name')}       style={{ cursor: 'pointer' }}>Название {sortIcon(orderBy === 'name', order)}</th>
                        <th onClick={() => requestSort('category')}   style={{ cursor: 'pointer' }}>Категория {sortIcon(orderBy === 'category', order)}</th>
                        <th onClick={() => requestSort('id_measure')} style={{ cursor: 'pointer' }}>Ед. {sortIcon(orderBy === 'id_measure', order)}</th>
                        <th onClick={() => requestSort('price')}      style={{ cursor: 'pointer', textAlign: 'right' }}>Цена {sortIcon(orderBy === 'price', order)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((p) => (
                        <tr
                          key={p.id}
                          className="crmpage__row"
                          onClick={() => navigate(generatePath(ProductProfilePageRoute, { id: p.id }))}
                        >
                          <td className="crmpage__date">{p.id}</td>
                          <td>
                            <div className="crmpage__user-cell">
                              {p.product_photo ? (
                                <span
                                  className="crmpage__avatar"
                                  aria-hidden
                                  style={{ background: `url(${p.product_photo}) center/cover no-repeat`, color: 'transparent' }}
                                />
                              ) : (
                                <span className="crmpage__avatar" aria-hidden>
                                  {(p.name || '?').charAt(0).toUpperCase()}
                                </span>
                              )}
                              <div style={{ minWidth: 0 }}>
                                <div className="crmpage__primary">{p.name || '—'}</div>
                                {p.description && (
                                  <div className="crmpage__sub" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 380 }}>
                                    {p.description}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="crmpage__muted">{catNameById(p.id_product_category) || '—'}</td>
                          <td className="crmpage__muted">{p.id_measure ?? '—'}</td>
                          <td className="crmpage__num" style={{ textAlign: 'right' }}>{formatPrice(p.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </TableCard>
        </div>
      </div>
    </AdminShell>
  );
}
